// SSR-compat: showdown is CJS, vite's SSR-Node loader doesn't extract named
// exports reliably. Default-import + destructure works in both SSR and browser.
import showdownPkg from "showdown";
import { useEffect, useMemo, useRef, type FunctionComponent } from "react";
import { pixelSchriftSkalieren } from "../utils/pixelSchriftSkalieren";
import { filtereHtmlMitStand, type GefiltertesHtml } from "../utils/htmlFilter";

const { Converter } = showdownPkg;

const converter = new Converter({ openLinksInNewWindow: true });

/**
 * Welle 2.5 Bug 3 Fix (2026-05-21): isHtmlContent() Pre-Check entfernt.
 *
 * Showdown unterstützt HTML-Passthrough — bestehende HTML-Tags bleiben
 * unverändert, und Markdown-Patterns INNERHALB von HTML-Tags werden
 * trotzdem konvertiert. Beispiel PPI:
 *   Source: `<FONT SIZE=4>**Krim**</FONT>`
 *   Showdown-Output: `<p><FONT SIZE=4><strong>Krim</strong></FONT></p>`
 *
 * Der frühere Pre-Check hat HTML erkannt und Showdown übersprungen →
 * `**Krim**` blieb literal. Jetzt: converter.makeHtml() läuft immer.
 */

/** Markiert einen Kasten, dessen Text NICHT gefiltert werden konnte (nur Text). */
const NUR_TEXT = "data-nur-text";

interface MarkdownViewerProps {
    source: string;
}

/**
 * Slide-Text (HTML oder Markdown) → das HTML, das in die Seite geht.
 *
 * LETZTER SCHRITT IST DER FILTER (Jans Frage 02.10.2026, s. utils/htmlFilter):
 * Der Text stammt von Nutzern und ging bisher ungeprueft per
 * dangerouslySetInnerHTML in die Seite — auf dem Server des Viewers sogar
 * direkt ins ausgelieferte HTML. Alles, was die Schritte davor erzeugen oder
 * durchreichen, laeuft deshalb zum Schluss durch die Erlaubnisliste.
 */
export function slideTextZuHtml(source: string | null | undefined): string {
    return slideTextGefiltert(source).html;
}

/** Wie `slideTextZuHtml` — dazu, ob wirklich gefiltert werden konnte. */
export function slideTextGefiltert(source: string | null | undefined): GefiltertesHtml {
    // Welle 2.5 Bug 3: converter.makeHtml() läuft immer (Showdown unterstützt
    // HTML-Passthrough + konvertiert Markdown innerhalb von HTML-Tags).
    // Embedded-Container-Fix (Laura, 2026-07-30): Links im Slide-Text müssen in
    // einem NEUEN Tab öffnen. Rohe HTML-`<a>`-Tags aus dem Rich-Text-Editor
    // haben KEIN target → in einem eingebetteten <scrolly-container>-iframe
    // navigieren sie den Frame → framing-geschützte Seiten (z.B. weser-kurier.de)
    // antworten mit "Verbindung verweigert". Wir backen target="_blank" in den
    // HTML-String (überlebt Re-Renders, anders als eine imperative DOM-Mutation
    // auf dangerouslySetInnerHTML). Nur `<a>` OHNE bereits gesetztes target
    // werden angefasst (Showdowns openLinksInNewWindow-Links bleiben unberührt).
    const htmlRoh = converter
        .makeHtml(source ?? "")
        .replace(
            /<a\b(?![^>]*\btarget=)/gi,
            '<a target="_blank" rel="noopener noreferrer"',
        )
        // YouTube-Embeds IMMER über die Privacy-Enhanced-Domain: die normale
        // Embed-Domain leitet ohne Drittanbieter-Cookies in die Google-
        // Consent-Schleife um (»hat dich zu oft weitergeleitet« statt Video,
        // Jans Fund 09.08.2026). Repariert eingebackene Bestands-Inhalte in
        // ALLEN Ansichten; neue Embeds erzeugt toEmbedUrl gleich richtig.
        // Greift nicht auf youtube-nocookie (idempotent).
        .replace(/(www\.)?youtube\.com\/embed\//gi, 'www.youtube-nocookie.com/embed/');
    // Feste Pixel-Schriftgroessen wachsen mit der Box (s. utils/pixelSchriftSkalieren).
    return filtereHtmlMitStand(pixelSchriftSkalieren(htmlRoh));
}

/**
 * MarkdownViewer renders slide-text content (HTML or markdown) into the
 * DOM via dangerouslySetInnerHTML, then walks the resulting subtree for
 * <video data-autoplay="1"> elements and registers a single
 * IntersectionObserver that drives play()/pause() based on viewport
 * visibility. Also calls .play() once the user has interacted with the
 * page so the muted-autoplay browser policy is satisfied.
 *
 * The observer is created once per slide-render and torn down on cleanup.
 * Native uploaded videos in slide texts now play when their slide scrolls
 * into view and pause when scrolled past — without the user clicking
 * anything. iframe-based YouTube/Vimeo embeds are NOT touched here; they
 * keep their click-to-play behavior.
 */
const MarkdownViewer: FunctionComponent<MarkdownViewerProps> = ({ source }) => {
    // Einmal je Text rechnen, nicht bei jedem Bild: Der Player rendert waehrend
    // der Fahrt staendig neu, Wandeln und Filtern haengen aber nur am Text.
    const { html, gefiltert } = useMemo(() => slideTextGefiltert(source), [source]);

    /**
     * WARUM hier ein useMemo steht (Jans Fund 23.09.2026, eingebettete Seite):
     *
     * React 19 vergleicht die Eigenschaft `dangerouslySetInnerHTML` ueber die
     * IDENTITAET des uebergebenen Objekts, nicht ueber den Text darin
     * (updateProperties -> setProp). Ein frisch gebautes `{ __html: html }`
     * ist bei jedem Rendern ein NEUES Objekt — React schreibt den Inhalt der
     * Box also jedes Mal neu, obwohl sich kein Zeichen geaendert hat.
     *
     * Der Player rendert waehrend der Fahrt bei JEDEM Bild. Gemessen in der
     * Vorschau: 271 Neuaufbauten in 5 Sekunden, obwohl gar nicht gescrollt
     * wurde. Fuer Text faellt das nicht auf; eine eingebettete Seite
     * (`<iframe>`) wird dabei aber jedes Mal weggeworfen und faengt von vorn
     * an zu laden — sie wird nie fertig und bleibt weiss.
     *
     * Mit dem Memo bleibt das Objekt gleich, solange der Text gleich ist:
     * React laesst den Kasten in Ruhe, das Fenster laedt einmal und bleibt.
     */
    const innenHtml = useMemo(() => ({ __html: html }), [html]);
    const containerRef = useRef<HTMLDivElement>(null);

    /**
     * NOTBEHELF DES SERVERS NACHHOLEN. Konnte der Viewer-Server einen Text
     * nicht filtern (zu lang, zu verschachtelt, Filter nicht eingerichtet),
     * liefert er ihn als reinen Text aus und markiert den Kasten. React laesst
     * beim Uebernehmen der Server-Seite den Inhalt des Kastens stehen — ohne
     * diesen Schritt bliebe der Text fuer den Leser unformatiert. Hier filtert
     * der Browser selbst und setzt das Ergebnis ein.
     *
     * Steht VOR dem Video-Effekt: Der sucht im Kasten nach Videos.
     */
    useEffect(() => {
        const root = containerRef.current;
        if (!root || !gefiltert || !root.hasAttribute(NUR_TEXT)) return;
        root.removeAttribute(NUR_TEXT);
        root.innerHTML = html;
    }, [html, gefiltert]);

    useEffect(() => {
        const root = containerRef.current;
        if (!root) return;
        const videos = Array.from(
            root.querySelectorAll<HTMLVideoElement>('video[data-autoplay="1"]'),
        );
        if (videos.length === 0) return;

        // Belt-and-braces: ensure the muted/loop/playsInline attrs are set
        // even when the HTML came from a path that didn't pre-set them.
        for (const v of videos) {
            v.muted = true;
            v.loop = true;
            v.playsInline = true;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    const video = entry.target as HTMLVideoElement;
                    if (entry.isIntersecting && entry.intersectionRatio >= 0.4) {
                        // Browser autoplay policy: ignore the rejection if it
                        // happens (no audio so policy *should* allow it).
                        video.play().catch(() => {});
                    } else {
                        video.pause();
                    }
                }
            },
            { threshold: [0, 0.4, 0.8] },
        );

        for (const v of videos) observer.observe(v);
        return () => observer.disconnect();
    }, [html]);

    return (
        <div
            ref={containerRef}
            {...(gefiltert ? {} : { [NUR_TEXT]: "" })}
            dangerouslySetInnerHTML={innenHtml}
        />
    );
};

export default MarkdownViewer;
